import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import { ACCESS_TOKEN_STORAGE_KEY } from '@okta/okta-auth-js';
import { getBranch, getMarket, getServiceInfo, getShowrooms } from '../../actions/location-actions';
import { getSessionStorage, getLocalStorage, getCookie, setSessionStorage } from '../utilities/functions';
import { useOktaAuth } from '../../hooks/use-okta-auth';
import { useAccount } from '../../hooks/use-account';
import { useCart } from '../../hooks/use-cart';
import { useProduct } from '../../hooks/use-product';
import { useIsClient } from '@uidotdev/usehooks';
import { useRouteChange } from '../../hooks/use-route-change';
import { HatchChat } from '../third-party/hatch-chat';
import { MidAtlanticChat } from '../third-party/mid-atlantic-chat';

import Header from '../common/header';
import Footer from '../common/footer';
import ToTop from '../common/to-top';
import LeadGenForm from '../common/footer/leadgen-form';
import ModalCustomerType from '../common/modal-customer-type';
import ModalSignedOut from '../common/modal-signedout';
import RTAPopout from './rta-popout';
import FeedbackSurvey from '../common/forms/feedback-survey';

import './index.css';

const Layout = (props) => {
	const { id, children, className, hideWarranty, showToggle = false, signInToggle = false, topLevelClass, hideHoverCart = false, hideLeadGen = false, hideHeader, footerComponent, pageType, pageTitle, ...other } = props;
	const skipTo = useRef();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const [subscribed, setSubscribed] = useState(false);
	const [profileSet, setProfileSet] = useState(false);
	const { isLogged, oktaInitialized, profile, setProfile, setAuthState, oktaAuth } = useOktaAuth();
	const { getCart } = useCart();
	const { getUser } = useAccount();
	const { fetchProductTiles } = useProduct();
	const isClient = useIsClient();

	const verifyUserIntegrityOnRenew = async (key, newToken) => {
		const token = newToken?.accessToken || newToken;
		if (key === ACCESS_TOKEN_STORAGE_KEY) {
			try {
				const user = await getUser(token);

				if (!user?.data?.profile) {
					/* eslint-disable no-console -- Allow error log */
					console.error('Critical error - Failed to validate user integrity');
					console.error(error);
					/* eslint-enable no-console -- Allow error log */
				}

				await getCart(token);
			} catch {}
		}
	};

	useEffect(() => {
		const authSession = getLocalStorage('authSession');
		const authValue = authSession ? authSession : { isPro: false };

		setProfile({
			...authValue,
			date: Date.now(),
		});
		setProfileSet(true);

		if (!subscribed) {
			oktaAuth.tokenManager.on('renewed', (key, newToken) => {
				verifyUserIntegrityOnRenew(key, newToken);
			});
			oktaAuth.authStateManager.subscribe(setAuthState);
			oktaAuth.start();

			setSubscribed(true);
		}

		return () => {
			if (subscribed) {
				oktaAuth.tokenManager.off('renewed');
				oktaAuth.authStateManager.unsubscribe(setAuthState);
				oktaAuth.stop();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- skip setProfile, setAuthState, tried callback on those and get same thing there.
	}, [oktaAuth, subscribed]);

	useRouteChange({
		profile,
		pageType: pageType,
		pageTitle: pageTitle,
		isLogged,
		ecommerce: locations.ecommerce,
	});

	useEffect(() => {
		if (getLocalStorage('UserLocation') != null) {
			const userInfo = getLocalStorage('UserLocation');
			if (userInfo && locations.market == undefined) {
				dispatch(getMarket(userInfo.postal_code));
				dispatch(getShowrooms(userInfo.postal_code));
				dispatch(getBranch(userInfo.postal_code));
				dispatch(getServiceInfo(userInfo.postal_code));
			}
		}
	}, [locations.market, dispatch]);

	useEffect(() => {
		if (getLocalStorage('UserLocation') == null && locations.showroom && locations.market == undefined) {
			if (locations.showroom?.guest_location) {
				dispatch(getMarket(locations.showroom.guest_location.postal_code));
				dispatch(getBranch(locations.showroom.guest_location.postal_code));
				dispatch(getServiceInfo(locations.showroom.guest_location.postal_code));
				dispatch(getShowrooms(locations.showroom.guest_location.postal_code));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [JSON.stringify(locations.showroom)]);

	useEffect(() => {
		let loaded = true;

		if (loaded && oktaInitialized) {
			if (isLogged && !getSessionStorage('productsFetched')) {
				dispatch(fetchProductTiles());
				setSessionStorage('productsFetched', true);
			}

			if ((isLogged && profile.isPro) || (!isLogged && getCookie('CustomerType') === 'Professional')) {
				const whereToBuyLink = document.querySelector('header a[href="/where-to-buy/"]');
				if (whereToBuyLink) {
					whereToBuyLink.addEventListener('click', redirectWhereToBuy);
					return () => {
						whereToBuyLink.removeEventListener('click', redirectWhereToBuy);
						loaded = false;
					};
				}
			}
		}

		return () => (loaded = false);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- function causes infinite loop, fix later
	}, [oktaInitialized, isLogged, profile.isPro]);

	const onSkipContentClick = () => skipTo.current.focus();

	const redirectWhereToBuy = (e) => {
		e.preventDefault();
		navigate('/professionals/find-dealer/');
	};

	return (
		<div
			id='pella-site-content'
			className={`pellaLayout relative z-1 h-full ${topLevelClass || ''}`}
		>
			<a
				href='#content'
				className={`pellaSkip sr-only focus:not-sr-only focus:!absolute focus:left-1 focus:top-1 focus:z-30 focus:bg-white focus:px-4 focus:py-2`}
				onClick={onSkipContentClick}
			>
				Skip to Content
			</a>

			{!hideHeader && (
				<Header
					signInToggle={signInToggle}
					showToggle={showToggle}
					hideHoverCart={hideHoverCart}
				/>
			)}

			<div className={`pellaContentWrapper grid min-h-full grid-cols-1 grid-rows-[1fr_auto] max-lg:transition-all max-lg:duration-500`}>
				<div id={id || ''}>
					<main
						id='content'
						className={`relative ${!hideHeader ? 'mt-[59px] sm:mt-[61px] xl:mt-[97px] 2xl:mt-[98px]' : ''} ${className || ''}`}
						ref={skipTo}
						{...other}
					>
						{children}
					</main>
					<ToTop />
				</div>

				{!hideLeadGen && <LeadGenForm />}
				{footerComponent}
				{!footerComponent && <Footer />}
			</div>

			<RTAPopout />
			<ModalSignedOut />
			<ModalCustomerType />
			{/* Chats */}
			<HatchChat />
			<MidAtlanticChat />
			{isClient && getSessionStorage('monFormType') && <FeedbackSurvey formType={getSessionStorage('monFormType')} />}
		</div>
	);
};

Layout.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	hideWarranty: PropTypes.bool,
	id: PropTypes.string,
	showToggle: PropTypes.bool,
	signInToggle: PropTypes.bool,
	topLevelClass: PropTypes.string,
	hideHoverCart: PropTypes.bool,
	hideLeadGen: PropTypes.bool,
	hideHeader: PropTypes.bool,
	footerComponent: PropTypes.node,
	pageType: PropTypes.string,
	pageTitle: PropTypes.string,
};

export default Layout;
